import { palette5 } from '../utils/colorPalette';
import type { OutfitSlot, HairStyle, IconStyle } from '../types';

/**
 * Mini SVG icon renderer for outfit slots.
 * Each slot+iconStyle draws a distinct silhouette with 5-tone shading.
 * viewBox: 32×28
 */
export const OutfitIconRenderer = {
  render(slot: OutfitSlot, color: string, hairStyle?: HairStyle, iconStyle?: IconStyle): string {
    const c = palette5(color);

    switch (slot) {
      case 'top':    return _renderTop(c, iconStyle);
      case 'bottom': return _renderBottom(c, iconStyle);
      case 'hair':   return _renderHair(color, hairStyle ?? 'buns');
      case 'hairpin':return _renderHairpin(c, iconStyle);
      case 'glasses':return _renderGlasses(c, iconStyle);
      case 'bag':    return _renderBag(c, iconStyle);
      case 'shoes':  return _renderShoes(c, iconStyle);
      default:       return `<svg viewBox="0 0 32 28" xmlns="http://www.w3.org/2000/svg" width="32" height="28"></svg>`;
    }
  },

  // kept for backwards compat
  _renderHair(color: string, style: HairStyle): string {
    return _renderHair(color, style);
  },
};

// ─────────────────── helpers ────────────────────────────────────────────────
type C = ReturnType<typeof palette5>;

function svg(body: string): string {
  return `<svg class="pixel" viewBox="0 0 32 28" xmlns="http://www.w3.org/2000/svg" width="32" height="28">${body}</svg>`;
}
function r(x:number,y:number,w:number,h:number,fill:string,op?:number): string {
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}"${op!=null?` opacity="${op}"`:''}/>`;
}
function circle(cx:number,cy:number,rad:number,fill:string,op?:number): string {
  return `<circle cx="${cx}" cy="${cy}" r="${rad}" fill="${fill}"${op!=null?` opacity="${op}"`:''}/>`;
}
function poly(points:string,fill:string): string {
  return `<polygon points="${points}" fill="${fill}"/>`;
}

// ─────────────────── TOP ────────────────────────────────────────────────────
function _renderTop(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Hoodie ─────────────────────────────────────────────────
    case 'hoodie':
      return svg(
        r(6,2,6,3,c.deep)+r(20,2,6,3,c.deep)+   // shoulders
        r(2,5,28,2,c.deep)+                        // collar band
        r(2,7,28,18,c.base)+
        r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // hood loop at top
        r(10,1,12,4,c.base)+r(11,2,10,2,c.light)+r(13,1,6,1,c.deep)+
        // pocket
        r(8,17,16,6,c.shadow)+r(9,18,14,4,c.base)+
        circle(16,20,2,c.deep)
      );
    // ── Panda hoodie ────────────────────────────────────────────
    case 'catHoodie': case 'pandaSuit':
      return svg(
        r(2,5,28,2,'#1a1a1a')+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,'#1a1a1a')+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // panda face on chest
        circle(16,15,7,'#ffffff')+
        circle(14,14,2,'#1a1a1a')+circle(18,14,2,'#1a1a1a')+ // eyes
        circle(16,17,1,'#ff9999')+                             // nose
        r(14,18,1,1,'#1a1a1a')+r(17,18,1,1,'#1a1a1a')+       // mouth
        // panda ears on top
        circle(9,4,4,'#1a1a1a')+circle(23,4,4,'#1a1a1a')+
        circle(9,4,2,'#ffffff')+circle(23,4,2,'#ffffff')
      );
    case 'bearHoodie':
      return svg(
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // bear face on chest
        circle(16,16,6,c.shadow)+
        circle(14,15,2,c.deep)+circle(18,15,2,c.deep)+
        circle(16,18,2,'#cc6633')+                 // snout
        circle(16,17,1,c.deep)+                    // nose
        // bear ears
        circle(8,3,4,c.base)+circle(24,3,4,c.base)+
        circle(8,3,2,c.shadow)+circle(24,3,2,c.shadow)
      );
    case 'bunnyHoodie':
      return svg(
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // bunny ears (long)
        r(9,0,4,8,c.base)+r(19,0,4,8,c.base)+
        r(10,1,2,5,'#ffcccc')+r(20,1,2,5,'#ffcccc')+
        // face on chest
        circle(16,16,5,c.base)+
        circle(14,15,1,'#1a1a1a')+circle(18,15,1,'#1a1a1a')+
        circle(16,17,1,'#ff9999')
      );
    // ── Dress ───────────────────────────────────────────────────
    case 'dress': case 'dressUpper': case 'angelDress': case 'fairyDress':
      return svg(
        r(10,2,12,4,c.deep)+                      // shoulders
        r(8,6,16,6,c.base)+r(8,6,16,2,c.light)+  // bodice
        r(8,6,2,6,c.shadow)+r(22,6,2,6,c.shadow)+
        // skirt flare
        r(4,12,24,12,c.base)+r(4,12,24,2,c.light)+
        r(2,20,28,4,c.base)+r(2,23,28,1,c.deep)+
        r(4,12,2,12,c.shadow)+r(26,12,2,12,c.shadow)+
        // ruffle hem
        r(2,24,4,2,c.light)+r(8,24,4,2,c.light)+r(14,24,4,2,c.light)+r(20,24,4,2,c.light)+r(26,24,4,2,c.light)+
        // bow on bodice
        r(13,7,3,2,c.deep)+r(16,7,3,2,c.deep)+circle(16,8,1,'#ffffff',0.6)
      );
    case 'princessGown': case 'unicornDress':
      return svg(
        r(10,2,12,4,c.deep)+
        r(8,6,16,5,c.base)+r(8,6,16,2,c.light)+
        // wide gown
        r(2,11,28,14,c.base)+r(2,11,28,2,c.light)+r(2,24,28,2,c.deep)+
        r(0,18,32,6,c.base)+r(0,23,32,1,c.deep)+
        // sparkle details
        circle(8,16,2,c.light,0.7)+circle(16,19,2,c.light,0.7)+circle(24,16,2,c.light,0.7)+
        // star on chest
        r(14,7,4,2,c.rim)+r(15,6,2,4,c.rim)
      );
    case 'magicalGirl':
      return svg(
        r(10,2,12,4,'#ff6fa1')+
        r(8,6,16,5,c.base)+r(8,6,16,2,c.light)+
        r(2,11,28,14,c.base)+r(2,23,28,2,'#ff6fa1')+
        circle(16,9,2,'#ffd95c')+circle(10,14,1,'#ffd95c')+circle(22,14,1,'#ffd95c')+
        r(13,7,6,2,'#ffd700')+
        r(2,22,4,2,'#ffffff')+r(8,22,4,2,'#ffffff')+r(14,22,4,2,'#ffffff')+r(20,22,4,2,'#ffffff')+r(26,22,4,2,'#ffffff')
      );
    // ── Blazer ──────────────────────────────────────────────────
    case 'blazer':
      return svg(
        r(6,2,6,3,c.deep)+r(20,2,6,3,c.deep)+
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // lapels
        r(11,7,4,8,c.shadow)+r(17,7,4,8,c.shadow)+
        // buttons
        r(15,13,2,2,c.deep)+r(15,17,2,2,c.deep)+
        // pocket
        r(20,16,6,4,c.shadow)+r(21,17,4,2,c.base)
      );
    // ── Jacket ──────────────────────────────────────────────────
    case 'jacket':
      return svg(
        r(4,3,6,3,c.deep)+r(22,3,6,3,c.deep)+
        r(2,6,28,2,c.deep)+
        r(2,8,28,17,c.base)+r(2,8,28,2,c.light)+
        r(2,24,28,2,c.deep)+
        r(2,8,2,17,c.shadow)+r(28,8,2,17,c.shadow)+
        // open front
        r(13,8,6,16,c.shadow)+
        // collar
        r(11,8,4,4,c.deep)+r(17,8,4,4,c.deep)+
        // zip
        r(15,12,2,10,c.deep)
      );
    // ── Crop top ────────────────────────────────────────────────
    case 'cropTop':
      return svg(
        r(8,6,16,3,c.deep)+                       // straps
        r(4,9,24,10,c.base)+r(4,9,24,2,c.light)+
        r(4,18,24,2,c.deep)+
        r(4,9,2,10,c.shadow)+r(26,9,2,10,c.shadow)+
        // hem ruffle
        r(2,19,4,2,c.light)+r(8,19,4,2,c.light)+r(14,19,4,2,c.light)+r(20,19,4,2,c.light)+r(26,19,4,2,c.light)
      );
    // ── Tank top ────────────────────────────────────────────────
    case 'tankTop':
      return svg(
        r(10,3,4,4,c.deep)+r(18,3,4,4,c.deep)+   // thin straps
        r(6,7,20,17,c.base)+r(6,7,20,2,c.light)+
        r(6,23,20,2,c.deep)+
        r(6,7,2,17,c.shadow)+r(24,7,2,17,c.shadow)
      );
    // ── Sweater / cardigan ──────────────────────────────────────
    case 'sweater':
      return svg(
        r(6,2,6,3,c.base)+r(20,2,6,3,c.base)+
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // ribbed hem
        r(2,21,2,2,c.deep)+r(6,21,2,2,c.deep)+r(10,21,2,2,c.deep)+r(14,21,2,2,c.deep)+r(18,21,2,2,c.deep)+r(22,21,2,2,c.deep)+r(26,21,2,2,c.deep)+r(30,21,2,2,c.deep)+
        // cuff
        r(2,7,2,2,c.deep)+r(28,7,2,2,c.deep)
      );
    case 'cardigan':
      return svg(
        r(6,2,6,3,c.base)+r(20,2,6,3,c.base)+
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // open front with buttons
        r(14,7,4,16,c.shadow)+
        r(15,10,2,2,c.light)+r(15,15,2,2,c.light)+r(15,20,2,2,c.light)
      );
    // ── Turtleneck ──────────────────────────────────────────────
    case 'turtleneck':
      return svg(
        r(10,1,12,5,c.base)+r(10,1,12,2,c.light)+ // tall collar
        r(10,1,2,5,c.shadow)+r(20,1,2,5,c.shadow)+
        r(2,6,28,18,c.base)+r(2,6,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,6,2,18,c.shadow)+r(28,6,2,18,c.shadow)
      );
    // ── Blouse ──────────────────────────────────────────────────
    case 'blouse':
      return svg(
        r(8,2,6,4,c.deep)+r(18,2,6,4,c.deep)+
        r(2,6,28,18,c.base)+r(2,6,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,6,2,18,c.shadow)+r(28,6,2,18,c.shadow)+
        // ruffle collar
        r(10,2,12,4,c.light)+r(11,3,10,2,c.base)+
        // bow tie
        circle(16,10,2,c.deep)
      );
    // ── Padding / puffer ────────────────────────────────────────
    case 'padding': case 'puffer':
      return svg(
        r(4,2,8,3,c.deep)+r(20,2,8,3,c.deep)+
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        // puffer segments
        r(2,11,28,2,c.shadow)+r(2,16,28,2,c.shadow)+
        // zipper
        r(15,7,2,16,c.deep)
      );
    // ── Vest ────────────────────────────────────────────────────
    case 'vest':
      return svg(
        r(10,2,12,4,c.deep)+
        r(4,6,24,18,c.base)+r(4,6,24,2,c.light)+
        r(4,23,24,2,c.deep)+
        r(4,6,2,18,c.shadow)+r(26,6,2,18,c.shadow)+
        // armholes
        r(2,6,4,10,c.deep)+r(26,6,4,10,c.deep)+
        // buttons
        r(15,10,2,2,c.rim)+r(15,15,2,2,c.rim)+r(15,20,2,2,c.rim)
      );
    // ── Hanbok top ──────────────────────────────────────────────
    case 'hanbokTop':
      return svg(
        r(10,2,12,4,c.deep)+
        r(6,6,20,8,c.base)+r(6,6,20,2,c.light)+
        r(6,6,2,8,c.shadow)+r(24,6,2,8,c.shadow)+
        // hanbok ribbon (고름)
        r(12,8,4,12,c.deep)+r(14,9,2,10,c.base)+
        // wide sleeves
        r(0,8,8,8,c.base)+r(24,8,8,8,c.base)+
        r(0,8,8,2,c.light)+r(24,8,8,2,c.light)
      );
    // ── Romper ──────────────────────────────────────────────────
    case 'romper':
      return svg(
        r(10,2,12,3,c.deep)+
        r(8,5,16,6,c.base)+r(8,5,16,2,c.light)+
        r(8,5,2,6,c.shadow)+r(22,5,2,6,c.shadow)+
        r(4,11,24,13,c.base)+r(4,11,24,2,c.light)+
        r(4,23,24,2,c.deep)+
        r(4,11,2,13,c.shadow)+r(26,11,2,13,c.shadow)+
        r(14,11,4,13,c.shadow)  // center seam
      );
    // ── Mage robe ────────────────────────────────────────────────
    case 'mageRobe': case 'magicRobe':
      return svg(
        r(10,2,12,4,c.deep)+
        r(6,6,20,18,c.base)+r(6,6,20,2,c.light)+
        r(6,23,20,2,c.deep)+
        r(6,6,2,18,c.shadow)+r(24,6,2,18,c.shadow)+
        // stars on robe
        r(9,11,2,2,'#ffd95c')+r(21,11,2,2,'#ffd95c')+
        r(9,17,2,2,'#ffd95c')+r(21,17,2,2,'#ffd95c')+
        circle(16,14,3,'#ffd95c',0.5)+
        // wide sleeves
        r(0,8,8,10,c.base)+r(0,8,8,2,c.light)+
        r(24,8,8,10,c.base)+r(24,8,8,2,c.light)
      );
    // ── T-shirt (default) ────────────────────────────────────────
    case 'tshirt': default:
      return svg(
        r(6,2,6,3,c.deep)+r(20,2,6,3,c.deep)+
        r(2,5,28,2,c.deep)+
        r(2,7,28,18,c.base)+r(2,7,28,2,c.light)+
        r(2,23,28,2,c.deep)+
        r(2,7,2,18,c.shadow)+r(28,7,2,18,c.shadow)+
        r(15,9,2,2,c.deep)+r(15,14,2,2,c.deep)+r(15,19,2,2,c.deep)
      );
  }
}

// ─────────────────── BOTTOM ─────────────────────────────────────────────────
function _renderBottom(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Skirts ──────────────────────────────────────────────────
    case 'shortSkirt': case 'pleatedSkirt': case 'aLineSkirt':
      return svg(
        r(3,3,26,2,c.deep)+
        r(3,5,26,4,c.base)+r(3,5,26,2,c.light)+
        // flare
        r(1,9,30,14,c.base)+r(1,9,30,2,c.light)+
        r(1,22,30,2,c.deep)+
        r(1,9,2,14,c.shadow)+r(29,9,2,14,c.shadow)+
        // pleats
        r(8,9,2,14,c.shadow)+r(15,9,2,14,c.shadow)+r(22,9,2,14,c.shadow)
      );
    case 'longSkirt':
      return svg(
        r(4,2,24,2,c.deep)+
        r(4,4,24,4,c.base)+r(4,4,24,2,c.light)+
        // long flare
        r(2,8,28,17,c.base)+r(2,8,28,2,c.light)+
        r(0,21,32,4,c.base)+r(0,24,32,1,c.deep)+
        r(2,8,2,17,c.shadow)+r(28,8,2,17,c.shadow)+
        r(8,8,2,17,c.shadow)+r(16,8,2,17,c.shadow)+r(24,8,2,17,c.shadow)
      );
    case 'tutuSkirt':
      return svg(
        r(5,2,22,3,c.deep)+
        r(5,5,22,5,c.base)+r(5,5,22,2,c.light)+
        // tutu layers
        r(0,10,32,5,c.base)+r(0,10,32,2,c.light)+
        r(2,15,28,5,c.base)+r(2,15,28,2,c.light)+
        r(4,20,24,5,c.base)+r(4,20,24,2,c.light)+
        // ruffle detail
        r(0,14,4,1,c.light)+r(6,14,4,1,c.light)+r(12,14,4,1,c.light)+r(18,14,4,1,c.light)+r(24,14,4,1,c.light)+r(28,14,4,1,c.light)+
        r(2,19,4,1,c.light)+r(8,19,4,1,c.light)+r(14,19,4,1,c.light)+r(20,19,4,1,c.light)+r(26,19,4,1,c.light)
      );
    case 'hanbokSkirt':
      return svg(
        r(6,2,20,2,c.deep)+
        r(6,4,20,3,c.base)+r(6,4,20,2,c.light)+
        // full wide hanbok skirt
        r(0,7,32,18,c.base)+r(0,7,32,2,c.light)+
        r(0,24,32,2,c.deep)+
        r(0,7,2,18,c.shadow)+r(30,7,2,18,c.shadow)+
        // horizontal bands
        r(0,13,32,2,c.shadow)+r(0,19,32,2,c.shadow)
      );
    case 'bubbleSkirt': case 'flowerSkirt':
      return svg(
        r(4,2,24,3,c.deep)+
        r(4,5,24,3,c.base)+r(4,5,24,2,c.light)+
        // bubble puff shape
        circle(8,14,7,c.base)+circle(16,14,7,c.base)+circle(24,14,7,c.base)+
        circle(8,14,5,c.light,0.4)+circle(16,14,5,c.light,0.4)+circle(24,14,5,c.light,0.4)+
        r(0,21,32,4,c.deep)
      );
    case 'skirtPants':
      return svg(
        r(3,2,26,3,c.deep)+
        r(3,5,26,5,c.base)+r(3,5,26,2,c.light)+
        // skirt overlay
        r(1,10,30,8,c.base)+r(1,10,30,2,c.light)+
        r(1,17,30,1,c.deep)+
        // pants under
        r(3,10,12,14,c.base)+r(17,10,12,14,c.base)+
        r(15,10,2,14,c.deep)+
        r(3,23,12,2,c.deep)+r(17,23,12,2,c.deep)
      );
    case 'sequinSkirt':
      return svg(
        r(3,3,26,2,c.deep)+
        r(3,5,26,4,c.base)+r(3,5,26,2,c.light)+
        r(1,9,30,16,c.base)+r(1,9,30,2,c.light)+r(1,24,30,2,c.deep)+
        // sparkle sequins
        circle(7,13,2,c.rim,0.9)+circle(14,16,2,c.rim,0.9)+circle(21,13,2,c.rim,0.9)+
        circle(10,20,1,'#ffffff',0.8)+circle(18,20,1,'#ffffff',0.8)+circle(25,17,1,'#ffffff',0.8)+
        r(5,11,2,2,c.light)+r(19,12,2,2,c.light)
      );
    // ── Pants types ─────────────────────────────────────────────
    case 'jeans':
      return svg(
        r(3,3,26,2,c.deep)+r(3,5,26,20,c.base)+r(3,5,26,2,c.light)+
        r(3,5,2,20,c.shadow)+r(27,5,2,20,c.shadow)+
        r(15,5,2,20,c.deep)+
        r(3,23,12,2,c.deep)+r(17,23,12,2,c.deep)+
        // rivets
        r(4,7,2,2,c.rim)+r(26,7,2,2,c.rim)
      );
    case 'leggings':
      return svg(
        r(4,3,24,2,c.deep)+r(4,5,24,20,c.base)+r(4,5,24,2,c.light)+
        r(4,5,2,20,c.shadow)+r(26,5,2,20,c.shadow)+
        r(15,5,2,20,c.deep)+
        r(4,24,11,2,c.deep)+r(17,24,11,2,c.deep)+
        // sheen
        r(6,7,2,14,c.light,0.4)
      );
    case 'widePants':
      return svg(
        r(3,3,26,2,c.deep)+
        r(1,5,30,20,c.base)+r(1,5,30,2,c.light)+
        r(1,5,2,20,c.shadow)+r(29,5,2,20,c.shadow)+
        r(14,5,4,20,c.shadow)+
        r(1,24,13,2,c.deep)+r(18,24,13,2,c.deep)
      );
    case 'joggers':
      return svg(
        r(4,3,24,2,c.deep)+r(4,5,24,18,c.base)+r(4,5,24,2,c.light)+
        r(4,5,2,18,c.shadow)+r(26,5,2,18,c.shadow)+
        r(15,5,2,18,c.deep)+
        // tapered cuffs
        r(5,22,10,4,c.base)+r(17,22,10,4,c.base)+
        r(5,22,10,1,c.deep)+r(17,22,1,10,c.deep)+
        r(5,25,10,1,c.deep)+r(17,25,10,1,c.deep)
      );
    case 'bikiniBottom':
      return svg(
        r(5,8,22,4,c.deep)+r(6,9,20,4,c.base)+r(6,9,20,1,c.light)+
        r(8,13,6,8,c.base)+r(18,13,6,8,c.base)+
        r(8,20,6,2,c.deep)+r(18,20,6,2,c.deep)+
        r(14,8,4,8,c.deep)+
        // ties
        r(3,8,3,2,c.shadow)+r(26,8,3,2,c.shadow)
      );
    // ── Shorts (default bottom) ──────────────────────────────────
    case 'jumpsuitBottom': default:
      return svg(
        r(3,3,26,2,c.deep)+
        r(3,5,26,20,c.base)+r(3,5,26,2,c.light)+
        r(3,5,2,20,c.shadow)+r(27,5,2,20,c.shadow)+
        r(15,5,2,20,c.deep)+
        r(3,23,12,2,c.deep)+r(17,23,12,2,c.deep)
      );
  }
}

// ─────────────────── HAIR ───────────────────────────────────────────────────
function _renderHair(color: string, style: HairStyle): string {
  const hc = palette5(color);

  switch (style) {
    case 'buns':
      return svg(
        r(3,4,8,8,hc.shadow)+r(4,3,7,9,hc.base)+r(5,5,3,3,hc.light)+
        r(21,4,8,8,hc.shadow)+r(21,3,7,9,hc.base)+r(22,5,3,3,hc.light)+
        r(9,11,14,2,hc.deep)+
        r(6,13,20,10,hc.base)+r(6,13,20,2,hc.shadow)+r(6,21,20,2,hc.deep)+
        r(8,15,3,4,hc.light)
      );
    case 'twintails':
      return svg(
        r(2,14,4,11,hc.shadow)+r(3,14,3,11,hc.base)+r(2,23,4,2,hc.deep)+
        r(26,14,4,11,hc.shadow)+r(26,14,3,11,hc.base)+r(26,23,4,2,hc.deep)+
        r(6,6,20,2,hc.deep)+r(4,8,24,10,hc.base)+r(4,8,24,2,hc.shadow)+
        r(4,16,24,2,hc.deep)+r(6,10,4,3,hc.light)
      );
    case 'pony':
      return svg(
        r(6,6,20,2,hc.deep)+r(4,8,24,10,hc.base)+r(4,8,24,2,hc.shadow)+
        r(4,16,24,2,hc.deep)+r(6,10,4,3,hc.light)+
        r(22,14,3,2,'#1a1a1a')+
        r(25,13,5,14,hc.shadow)+r(25,13,4,14,hc.base)+r(25,25,5,2,hc.deep)+
        r(26,15,2,6,hc.light)
      );
    case 'bob':
      return svg(
        r(6,4,20,2,hc.deep)+r(4,6,24,16,hc.base)+r(4,6,24,2,hc.shadow)+
        r(4,20,24,2,hc.deep)+r(2,20,2,2,hc.deep)+r(28,20,2,2,hc.deep)+
        r(6,9,3,8,hc.light)+r(23,9,3,8,hc.light)
      );
    case 'rainbow_long':
      return svg(
        r(2,4,28,4,'#ff5050')+r(2,8,28,4,'#ff9933')+
        r(2,12,28,4,'#ffd95c')+r(2,16,28,4,'#7fdc6c')+
        r(2,20,28,4,'#5cc8ff')+r(2,24,28,4,'#9b6bff')+
        r(4,4,24,1,'#ffffff')+r(28,12,2,2,'#ffffff')
      );
    case 'galaxy_long':
      return svg(
        r(4,4,24,2,'#1a0f3a')+r(2,6,28,22,'#2a1a5c')+
        r(4,10,2,2,'#ffffff')+r(20,8,2,2,'#ffffff')+
        r(8,16,2,2,'#ffd95c')+r(24,20,2,2,'#ffffff')+
        r(14,22,2,2,'#ffffff')+r(2,26,28,2,'#1a0f3a')
      );
    case 'fire_long':
      return svg(
        r(4,4,24,2,'#cc2020')+r(2,6,28,20,'#ff6022')+
        r(2,8,28,2,'#cc2020')+r(4,10,3,14,'#ffaa20')+r(25,10,3,14,'#ffaa20')+
        r(8,18,2,2,'#ffaa20')+r(20,16,2,2,'#ffaa20')+
        // 끝 들쭉
        poly('2,26 6,30 10,26 14,30 18,26 22,30 26,26 30,30','#cc2020')
      );
    case 'silver_flower':
      return svg(
        r(4,4,24,2,'#9898a8')+r(2,6,28,20,'#c8c8d8')+
        r(2,6,28,4,'#e8e8f0')+r(4,10,3,14,'#ffffff')+r(25,10,3,14,'#ffffff')+
        // 꽃 4잎
        r(2,12,3,3,'#ffb0d0')+r(7,9,3,3,'#ffb0d0')+r(2,17,3,3,'#ffb0d0')+r(7,20,3,3,'#ffb0d0')+r(5,15,2,2,'#ffd95c')+
        r(22,12,3,3,'#ffb0d0')+r(27,9,3,3,'#ffb0d0')+r(22,17,3,3,'#ffb0d0')+r(27,20,3,3,'#ffb0d0')+r(25,15,2,2,'#ffd95c')
      );
    case 'pastel_mermaid':
      return svg(
        r(2,4,28,6,'#7feedb')+r(2,10,28,6,'#ffb0d0')+
        r(2,16,28,6,'#7feedb')+r(2,22,28,6,'#ffb0d0')+
        r(4,4,24,1,'#d8c8ff')+r(4,16,24,1,'#d8c8ff')
      );
    case 'long': default:
      return svg(
        r(4,4,24,2,hc.deep)+r(2,6,28,20,hc.base)+r(2,6,28,2,hc.shadow)+
        r(2,14,28,1,hc.shadow)+r(2,20,28,1,hc.shadow)+r(2,26,28,2,hc.deep)+
        r(4,9,3,14,hc.light)+r(25,9,3,14,hc.light)
      );
  }
}

// ─────────────────── HAIRPIN ────────────────────────────────────────────────
function _renderHairpin(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Star ───────────────────────────────────────────────────
    case 'starPin':
      return svg(
        // big 5-point star
        poly('16,2 18,10 26,10 20,15 22,23 16,18 10,23 12,15 6,10 14,10','#ffd700')+
        poly('16,5 17.5,10 22,10 18.5,13 19.8,18 16,15 12.2,18 13.5,13 10,10 14.5,10','#ffffff')+
        circle(16,12,2,'#ff6fa1')
      );
    // ── Heart ──────────────────────────────────────────────────
    case 'heartPin':
      return svg(
        // heart shape
        `<path d="M16 24 C16 24 4 16 4 10 C4 6 7 4 10 4 C12 4 14 5 16 7 C18 5 20 4 22 4 C25 4 28 6 28 10 C28 16 16 24 16 24Z" fill="${c.base}"/>` +
        `<path d="M16 24 C16 24 4 16 4 10 C4 6 7 4 10 4 C12 4 14 5 16 7 C18 5 20 4 22 4 C25 4 28 6 28 10 C28 16 16 24 16 24Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        circle(11,8,2,c.light,0.6)
      );
    // ── Crown ─────────────────────────────────────────────────
    case 'crown': case 'bigCrown':
      return svg(
        // crown base
        r(4,14,24,10,c.base)+r(4,14,24,2,c.light)+r(4,23,24,2,c.deep)+
        // crown points
        poly('4,14 4,6 8,12 12,4 16,12 20,4 24,12 28,6 28,14','#ffd700')+
        r(4,14,24,2,c.rim)+
        // gems on crown
        circle(8,17,2,'#ff6fa1')+circle(16,17,2,'#9b6bff')+circle(24,17,2,'#6fd4c4')
      );
    // ── Tiara ─────────────────────────────────────────────────
    case 'tiara':
      return svg(
        r(4,16,24,6,c.base)+r(4,16,24,2,c.light)+r(4,21,24,2,c.deep)+
        // tiara arches
        `<path d="M4,16 Q10,8 16,6 Q22,8 28,16" fill="none" stroke="${c.rim}" stroke-width="2.5"/>` +
        `<path d="M7,16 Q12,10 16,9 Q20,10 25,16" fill="none" stroke="${c.base}" stroke-width="1.5"/>` +
        // gems
        circle(16,7,3,c.rim)+circle(10,13,2,'#ffffff',0.8)+circle(22,13,2,'#ffffff',0.8)
      );
    // ── Cat ears ───────────────────────────────────────────────
    case 'catEars':
      return svg(
        // cat ear triangles
        poly('2,18 8,4 14,18','#1a1a1a')+poly('18,18 24,4 30,18','#1a1a1a')+
        poly('4,16 8,7 12,16',c.base)+poly('20,16 24,7 28,16',c.base)+
        r(2,18,28,6,c.shadow)+r(2,18,28,2,c.light)+r(2,23,28,2,c.deep)
      );
    // ── Rabbit ears ────────────────────────────────────────────
    case 'rabbitEars': case 'bunnyEars':
      return svg(
        r(9,0,6,18,c.base)+r(17,0,6,18,c.base)+
        r(10,1,4,14,'#ffcccc')+r(18,1,4,14,'#ffcccc')+
        r(9,0,6,2,c.deep)+r(17,0,6,2,c.deep)+
        r(2,18,28,6,c.shadow)+r(2,18,28,2,c.light)+r(2,23,28,2,c.deep)
      );
    // ── Bear ears ──────────────────────────────────────────────
    case 'bearEars':
      return svg(
        circle(8,10,7,c.base)+circle(24,10,7,c.base)+
        circle(8,10,4,c.shadow)+circle(24,10,4,c.shadow)+
        r(2,16,28,8,c.shadow)+r(2,16,28,2,c.light)+r(2,23,28,2,c.deep)
      );
    // ── Panda ears ─────────────────────────────────────────────
    case 'pandaEars':
      return svg(
        circle(8,9,7,'#1a1a1a')+circle(24,9,7,'#1a1a1a')+
        circle(8,9,4,'#ffffff')+circle(24,9,4,'#ffffff')+
        r(2,15,28,9,c.shadow)+r(2,15,28,2,c.light)+r(2,23,28,2,c.deep)
      );
    // ── Beret ─────────────────────────────────────────────────
    case 'beretCap':
      return svg(
        `<ellipse cx="16" cy="11" rx="14" ry="10" fill="${c.base}"/>` +
        `<ellipse cx="16" cy="11" rx="14" ry="10" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        `<ellipse cx="16" cy="11" rx="10" ry="5" fill="${c.light}" opacity="0.3"/>` +
        r(8,20,16,4,c.deep)+r(8,20,16,2,c.shadow)+
        circle(16,8,2,c.rim)
      );
    // ── Wizard hat ─────────────────────────────────────────────
    case 'wizardHat':
      return svg(
        // tall cone
        poly(`16,0 10,16 22,16`,c.base)+poly(`16,2 11,15 21,15`,c.light)+
        // brim
        r(2,16,28,5,c.base)+r(2,16,28,2,c.light)+r(2,20,28,2,c.deep)+
        // star
        r(14,6,2,4,'#ffd95c')+r(13,7,4,2,'#ffd95c')
      );
    // ── Cap ───────────────────────────────────────────────────
    case 'cap':
      return svg(
        `<ellipse cx="16" cy="14" rx="14" ry="8" fill="${c.base}"/>` +
        r(2,12,28,6,c.base)+r(2,12,28,2,c.light)+r(2,17,28,2,c.deep)+
        // brim
        r(0,17,32,4,c.shadow)+r(0,17,32,1,c.deep)+
        // button on top
        circle(16,14,3,c.deep)+circle(16,14,2,c.light)
      );
    // ── Halo ──────────────────────────────────────────────────
    case 'halo':
      return svg(
        `<ellipse cx="16" cy="8" rx="12" ry="5" fill="none" stroke="#ffd700" stroke-width="3"/>` +
        `<ellipse cx="16" cy="8" rx="12" ry="5" fill="none" stroke="#ffffff" stroke-width="1.5" opacity="0.6"/>` +
        r(2,18,28,8,c.shadow)+r(2,18,28,2,c.light)+r(2,25,28,2,c.deep)+
        // tiny sparkles
        circle(6,5,1,'#ffd700')+circle(26,5,1,'#ffd700')+circle(16,3,1,'#ffffff')
      );
    // ── Devil horns ────────────────────────────────────────────
    case 'devilHorns':
      return svg(
        poly('6,16 4,4 12,14','#cc0000')+poly('26,16 28,4 20,14','#cc0000')+
        poly('7,14 5,7 11,13','#ff3333')+poly('25,14 27,7 21,13','#ff3333')+
        r(2,16,28,8,c.shadow)+r(2,16,28,2,c.light)+r(2,23,28,2,c.deep)
      );
    // ── Straw hat ──────────────────────────────────────────────
    case 'strawHat':
      return svg(
        `<ellipse cx="16" cy="12" rx="10" ry="7" fill="${c.base}"/>` +
        r(0,16,32,5,c.base)+r(0,16,32,1,c.deep)+r(0,20,32,1,c.deep)+
        `<ellipse cx="16" cy="12" rx="10" ry="7" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        // weave detail
        r(8,10,16,1,c.shadow)+r(8,13,16,1,c.shadow)+
        // ribbon
        r(4,16,24,3,'#ff6fa1')
      );
    // ── Flower pin ─────────────────────────────────────────────
    case 'flowerPin': case 'sakuraPin': case 'butterflyPin':
      return svg(
        circle(10,9,6,c.base)+circle(22,9,6,c.base)+
        circle(10,18,6,c.base)+circle(22,18,6,c.base)+
        circle(16,13,5,c.light)+
        circle(10,9,3,'#ffffff',0.4)+circle(22,9,3,'#ffffff',0.4)+
        circle(10,18,3,'#ffffff',0.4)+circle(22,18,3,'#ffffff',0.4)+
        circle(16,13,2,'#ffd95c')
      );
    // ── Unicorn horn ───────────────────────────────────────────
    case 'unicornHorn':
      return svg(
        poly('16,0 12,18 20,18','#ffd700')+poly('16,2 13,17 19,17','#ffffff')+
        r(12,17,8,2,c.base)+
        // rainbow swirl
        r(14,5,4,1,'#ff6fa1')+r(13,9,6,1,'#9b6bff')+r(12,13,8,1,'#6fd4c4')+
        r(2,19,28,7,c.shadow)+r(2,19,28,2,c.light)+r(2,25,28,2,c.deep)
      );
    // ── Beanie ─────────────────────────────────────────────────
    case 'beanie':
      return svg(
        `<ellipse cx="16" cy="10" rx="13" ry="9" fill="${c.base}"/>` +
        `<ellipse cx="16" cy="10" rx="13" ry="9" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        r(3,17,26,5,c.deep)+r(3,17,26,2,c.shadow)+
        // pompom
        circle(16,2,4,c.light)+circle(16,2,2,'#ffffff',0.5)+
        // ribbing lines
        r(8,11,2,8,c.shadow)+r(14,11,2,8,c.shadow)+r(20,11,2,8,c.shadow)
      );
    // ── Ribbon (default) ───────────────────────────────────────
    case 'ribbon': case 'bigRibbon': default:
      return svg(
        r(2,9,6,11,c.deep)+r(3,10,5,9,c.base)+r(3,10,2,3,c.light)+
        r(24,9,6,11,c.deep)+r(24,10,5,9,c.base)+r(24,10,2,3,c.light)+
        r(8,11,16,6,c.deep)+r(9,12,14,4,c.base)+r(9,12,3,2,c.light)+
        r(13,11,6,7,c.deep)+r(14,12,4,5,c.shadow)
      );
  }
}

// ─────────────────── GLASSES ────────────────────────────────────────────────
function _renderGlasses(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Heart frames ───────────────────────────────────────────
    case 'heartShades':
      return svg(
        `<path d="M8 14 C8 14 2 10 2 7 C2 5 3.5 4 5 4 C6.2 4 7.2 4.6 8 6 C8.8 4.6 9.8 4 11 4 C12.5 4 14 5 14 7 C14 10 8 14 8 14Z" fill="${c.base}"/>` +
        `<path d="M8 14 C8 14 2 10 2 7 C2 5 3.5 4 5 4 C6.2 4 7.2 4.6 8 6 C8.8 4.6 9.8 4 11 4 C12.5 4 14 5 14 7 C14 10 8 14 8 14Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        `<path d="M24 14 C24 14 18 10 18 7 C18 5 19.5 4 21 4 C22.2 4 23.2 4.6 24 6 C24.8 4.6 25.8 4 27 4 C28.5 4 30 5 30 7 C30 10 24 14 24 14Z" fill="${c.base}"/>` +
        `<path d="M24 14 C24 14 18 10 18 7 C18 5 19.5 4 21 4 C22.2 4 23.2 4.6 24 6 C24.8 4.6 25.8 4 27 4 C28.5 4 30 5 30 7 C30 10 24 14 24 14Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        r(14,8,4,2,c.deep)+r(0,14,2,6,c.deep)+r(30,14,2,6,c.deep)+
        circle(5,8,1,'#ffffff',0.7)+circle(21,8,1,'#ffffff',0.7)
      );
    // ── Star shades ────────────────────────────────────────────
    case 'starShades':
      return svg(
        poly('8,2 9.5,7 14,7 10.5,10 11.5,15 8,12 4.5,15 5.5,10 2,7 6.5,7',c.base)+
        poly('8,4 9,7.5 12,7.5 9.8,9.3 10.5,12 8,10.5 5.5,12 6.2,9.3 4,7.5 7,7.5','#ffffff')+
        poly('24,2 25.5,7 30,7 26.5,10 27.5,15 24,12 20.5,15 21.5,10 18,7 22.5,7',c.base)+
        poly('24,4 25,7.5 28,7.5 25.8,9.3 26.5,12 24,10.5 21.5,12 22.2,9.3 20,7.5 23,7.5','#ffffff')+
        r(14,9,4,2,c.deep)
      );
    // ── Cat eye ────────────────────────────────────────────────
    case 'catEyeShades':
      return svg(
        // left lens – angled top-right corner
        `<polygon points="2,9 13,7 13,18 2,18" fill="${c.base}" opacity="0.5"/>` +
        `<polygon points="2,9 13,7 13,18 2,18" fill="none" stroke="${c.deep}" stroke-width="2"/>` +
        // right lens
        `<polygon points="19,7 30,9 30,18 19,18" fill="${c.base}" opacity="0.5"/>` +
        `<polygon points="19,7 30,9 30,18 19,18" fill="none" stroke="${c.deep}" stroke-width="2"/>` +
        r(13,11,6,2,c.deep)+r(0,17,2,6,c.deep)+r(30,17,2,6,c.deep)+
        r(3,9,3,3,c.light,0.5)+r(19,8,3,3,c.light,0.5)
      );
    // ── Square glasses ─────────────────────────────────────────
    case 'squareGlasses':
      return svg(
        r(2,9,11,10,c.deep)+r(3,10,9,8,c.base,0.4)+r(3,10,3,3,c.light,0.6)+
        r(19,9,11,10,c.deep)+r(20,10,9,8,c.base,0.4)+r(20,10,3,3,c.light,0.6)+
        r(13,13,6,2,c.deep)+r(0,18,2,6,c.deep)+r(30,18,2,6,c.deep)
      );
    // ── Aviator ────────────────────────────────────────────────
    case 'aviatorShades':
      return svg(
        // teardrop shapes
        `<path d="M2,9 Q7,6 13,9 Q13,18 7,20 Q2,18 2,9Z" fill="${c.base}" opacity="0.5"/>` +
        `<path d="M2,9 Q7,6 13,9 Q13,18 7,20 Q2,18 2,9Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        `<path d="M19,9 Q25,6 30,9 Q30,18 25,20 Q19,18 19,9Z" fill="${c.base}" opacity="0.5"/>` +
        `<path d="M19,9 Q25,6 30,9 Q30,18 25,20 Q19,18 19,9Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        r(13,11,6,2,c.deep)+r(0,18,2,6,c.deep)+r(30,18,2,6,c.deep)+
        r(3,10,3,3,c.light,0.5)+r(20,10,3,3,c.light,0.5)
      );
    // ── Monocle ────────────────────────────────────────────────
    case 'monocle':
      return svg(
        circle(12,14,9,c.deep)+circle(12,14,7,c.base,0.4)+circle(12,14,7,'none')+
        circle(12,14,4,c.light,0.3)+
        `<circle cx="12" cy="14" r="9" fill="none" stroke="${c.deep}" stroke-width="2"/>` +
        // chain
        `<path d="M21,14 Q24,14 26,18 L28,22" fill="none" stroke="${c.rim}" stroke-width="1.5"/>`
      );
    // ── VR headset ─────────────────────────────────────────────
    case 'vrHeadset':
      return svg(
        r(2,7,28,16,c.deep)+r(3,8,26,14,c.base)+r(3,8,26,3,c.light)+
        // lenses
        r(5,11,10,8,c.shadow)+r(17,11,10,8,c.shadow)+
        circle(10,15,3,'#6fd4c4',0.5)+circle(22,15,3,'#6fd4c4',0.5)+
        r(5,11,3,3,c.light,0.3)+r(17,11,3,3,c.light,0.3)+
        // nose bridge
        r(14,14,4,4,c.deep)+
        // strap
        r(0,11,3,6,c.deep)+r(29,11,3,6,c.deep)
      );
    // ── 3D glasses ─────────────────────────────────────────────
    case 'threeD':
      return svg(
        r(2,9,11,10,c.deep)+r(3,10,9,8,'#ff3333',0.4)+r(3,10,3,3,'#ffffff',0.4)+
        r(19,9,11,10,c.deep)+r(20,10,9,8,'#3333ff',0.4)+r(20,10,3,3,'#ffffff',0.4)+
        r(13,13,6,2,c.deep)+r(0,18,2,6,c.deep)+r(30,18,2,6,c.deep)
      );
    // ── Round glasses (default) ────────────────────────────────
    case 'roundGlasses': default:
      return svg(
        r(2,9,11,2,c.deep)+r(2,11,2,8,c.deep)+r(11,11,2,8,c.deep)+
        r(2,19,11,2,c.deep)+r(4,11,7,8,c.base,0.4)+r(4,11,3,3,c.light,0.7)+
        r(19,9,11,2,c.deep)+r(19,11,2,8,c.deep)+r(28,11,2,8,c.deep)+
        r(19,19,11,2,c.deep)+r(21,11,7,8,c.base,0.4)+r(21,11,3,3,c.light,0.7)+
        r(13,13,6,2,c.deep)
      );
  }
}

// ─────────────────── BAG ────────────────────────────────────────────────────
function _renderBag(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Backpack ───────────────────────────────────────────────
    case 'backpack':
      return svg(
        // straps
        r(9,2,4,6,c.shadow)+r(19,2,4,6,c.shadow)+
        // body
        r(5,8,22,18,c.base)+r(5,8,22,2,c.light)+r(5,25,22,2,c.deep)+
        r(5,8,2,18,c.shadow)+r(25,8,2,18,c.shadow)+
        // pocket
        r(9,16,14,8,c.shadow)+r(10,17,12,6,c.base)+
        // zipper
        r(9,15,14,2,c.deep)+circle(16,15,1,c.rim)
      );
    // ── School bag ─────────────────────────────────────────────
    case 'schoolBag':
      return svg(
        r(9,2,4,4,c.shadow)+r(19,2,4,4,c.shadow)+
        r(4,6,24,20,c.base)+r(4,6,24,2,c.light)+r(4,25,24,2,c.deep)+
        r(4,6,2,20,c.shadow)+r(26,6,2,20,c.shadow)+
        r(9,13,14,9,c.shadow)+r(10,14,12,7,c.base)+
        r(9,13,14,2,c.deep)+
        r(13,11,6,4,c.deep)+circle(16,11,2,c.rim) // buckle top
      );
    // ── Cat bag ────────────────────────────────────────────────
    case 'animalCatBag':
      return svg(
        // cat ears
        poly('6,10 2,2 10,8','#1a1a1a')+poly('26,10 30,2 22,8','#1a1a1a')+
        poly('7,9 3,4 9,8',c.base)+poly('25,9 29,4 23,8',c.base)+
        // cat face bag
        r(4,9,24,16,c.base)+r(4,9,24,2,c.light)+r(4,24,24,2,c.deep)+
        r(4,9,2,16,c.shadow)+r(26,9,2,16,c.shadow)+
        // face
        circle(12,16,2,c.deep)+circle(20,16,2,c.deep)+  // eyes
        circle(16,19,2,'#ff9999')+                        // nose
        r(13,20,1,1,c.deep)+r(18,20,1,1,c.deep)+          // mouth
        // whiskers
        r(2,17,8,1,c.shadow)+r(22,17,8,1,c.shadow)+
        r(2,19,8,1,c.shadow)+r(22,19,8,1,c.shadow)
      );
    // ── Bear bag ──────────────────────────────────────────────
    case 'animalBearBag': case 'bearBag':
      return svg(
        circle(9,10,7,c.base)+circle(23,10,7,c.base)+
        circle(9,10,4,c.shadow)+circle(23,10,4,c.shadow)+
        r(4,9,24,17,c.base)+r(4,9,24,2,c.light)+r(4,25,24,2,c.deep)+
        r(4,9,2,17,c.shadow)+r(26,9,2,17,c.shadow)+
        // bear face
        circle(12,17,2,c.deep)+circle(20,17,2,c.deep)+
        circle(16,20,3,'#cc6633')+circle(16,19,1,c.deep)+
        r(14,21,1,1,c.deep)+r(17,21,1,1,c.deep)
      );
    // ── Panda bag ─────────────────────────────────────────────
    case 'pandaBag': case 'animalPandaBag':
      return svg(
        circle(9,10,7,'#1a1a1a')+circle(23,10,7,'#1a1a1a')+
        circle(9,10,4,'#ffffff')+circle(23,10,4,'#ffffff')+
        r(4,9,24,17,'#ffffff')+r(4,9,24,2,'#eeeeee')+r(4,25,24,2,'#1a1a1a')+
        r(4,9,2,17,'#cccccc')+r(26,9,2,17,'#cccccc')+
        circle(11,17,2,'#1a1a1a')+circle(21,17,2,'#1a1a1a')+  // eyes
        circle(16,20,2,'#ff9999')+circle(16,19,1,'#1a1a1a')   // nose
      );
    // ── Unicorn bag ────────────────────────────────────────────
    case 'unicornBag':
      return svg(
        // horn
        poly('16,0 13,8 19,8','#ffd700')+
        r(4,8,24,18,c.base)+r(4,8,24,2,c.light)+r(4,25,24,2,c.deep)+
        r(4,8,2,18,c.shadow)+r(26,8,2,18,c.shadow)+
        // rainbow mane
        r(18,8,6,2,'#ff6fa1')+r(20,10,4,2,'#9b6bff')+r(20,12,3,2,'#6fd4c4')+
        // stars
        circle(10,16,2,'#ffd95c')+circle(16,19,2,'#ffd95c')+circle(22,16,2,'#ffd95c')+
        circle(14,14,1,'#ffffff')+circle(20,22,1,'#ffffff')
      );
    // ── Star bag ──────────────────────────────────────────────
    case 'starBag':
      return svg(
        // large star body
        poly('16,2 19,10 28,10 21,16 23,24 16,19 9,24 11,16 4,10 13,10','#ffd700')+
        poly('16,5 18,10 24,10 19.5,13.5 21,19 16,16 11,19 12.5,13.5 8,10 14,10',c.base)+
        circle(16,13,3,c.rim,0.6)+
        r(11,4,2,2,c.light,0.7)+r(19,8,2,2,c.light,0.7)
      );
    // ── Heart bag ─────────────────────────────────────────────
    case 'heartBag':
      return svg(
        `<path d="M16 26 C16 26 2 18 2 10 C2 6 5 4 8 4 C11 4 14 6 16 9 C18 6 21 4 24 4 C27 4 30 6 30 10 C30 18 16 26 16 26Z" fill="${c.base}"/>` +
        `<path d="M16 26 C16 26 2 18 2 10 C2 6 5 4 8 4 C11 4 14 6 16 9 C18 6 21 4 24 4 C27 4 30 6 30 10 C30 18 16 26 16 26Z" fill="none" stroke="${c.deep}" stroke-width="1.5"/>` +
        circle(9,10,2,c.light,0.6)+circle(22,10,2,c.light,0.4)+
        // handle
        r(13,3,6,2,c.deep)
      );
    // ── Tote bag ──────────────────────────────────────────────
    case 'totebag':
      return svg(
        // handles
        r(9,2,2,8,c.shadow)+r(21,2,2,8,c.shadow)+
        r(8,2,4,2,c.deep)+r(20,2,4,2,c.deep)+
        // open top bag
        r(4,8,24,18,c.base)+r(4,8,24,2,c.light)+r(4,25,24,2,c.deep)+
        r(4,8,2,18,c.shadow)+r(26,8,2,18,c.shadow)+
        // content visible at top
        r(6,9,20,3,c.light,0.3)
      );
    // ── Clutch bag ─────────────────────────────────────────────
    case 'clutch': case 'purse':
      return svg(
        r(4,8,24,16,c.base)+r(4,8,24,2,c.light)+r(4,23,24,2,c.deep)+
        r(4,8,2,16,c.shadow)+r(26,8,2,16,c.shadow)+
        // flap
        r(4,8,24,8,c.shadow)+r(4,8,24,2,c.light)+
        // clasp
        circle(16,12,3,c.rim)+circle(16,12,1,'#ffffff')
      );
    // ── Gem bag ────────────────────────────────────────────────
    case 'gemBag': case 'jewel':
      return svg(
        r(9,3,4,4,c.shadow)+r(19,3,4,4,c.shadow)+
        r(4,7,24,18,c.base)+r(4,7,24,2,c.light)+r(4,24,24,2,c.deep)+
        r(4,7,2,18,c.shadow)+r(26,7,2,18,c.shadow)+
        // jewel pattern
        circle(16,16,7,c.rim,0.6)+
        poly('16,10 18,15 24,15 19,18 21,24 16,21 11,24 13,18 8,15 14,15',c.light)+
        circle(16,16,3,'#ffffff',0.4)
      );
    // ── Flower bag ─────────────────────────────────────────────
    case 'flowerBag':
      return svg(
        r(9,2,4,5,c.shadow)+r(19,2,4,5,c.shadow)+
        r(4,7,24,18,c.base)+r(4,7,24,2,c.light)+r(4,24,24,2,c.deep)+
        r(4,7,2,18,c.shadow)+r(26,7,2,18,c.shadow)+
        // flower
        circle(16,16,4,c.rim)+circle(12,12,3,c.light)+circle(20,12,3,c.light)+
        circle(12,20,3,c.light)+circle(20,20,3,c.light)+
        circle(16,16,3,'#ffd95c')
      );
    // ── Gift box ──────────────────────────────────────────────
    case 'giftBox':
      return svg(
        r(4,11,24,15,c.base)+r(4,11,24,2,c.light)+r(4,25,24,2,c.deep)+
        r(4,11,2,15,c.shadow)+r(26,11,2,15,c.shadow)+
        r(4,11,24,5,c.shadow)+  // lid
        r(15,8,2,3,c.deep)+  // ribbon vertical
        r(4,14,24,2,c.deep)+  // ribbon horizontal
        // bow
        r(12,6,4,4,c.rim)+r(16,6,4,4,c.rim)+circle(16,8,2,'#ffffff',0.5)
      );
    // ── Cross bag (default) ────────────────────────────────────
    case 'crossBag': case 'messenger': default:
      return svg(
        r(11,3,10,2,c.deep)+r(9,4,2,4,c.shadow)+r(21,4,2,4,c.shadow)+
        r(4,8,24,2,c.deep)+r(4,10,24,14,c.base)+r(4,10,24,2,c.light)+
        r(4,22,24,2,c.deep)+r(4,10,2,14,c.shadow)+r(26,10,2,14,c.shadow)+
        r(13,14,6,4,c.deep)+r(14,15,4,2,c.shadow)
      );
  }
}

// ─────────────────── SHOES ──────────────────────────────────────────────────
function _renderShoes(c: C, style?: IconStyle): string {
  switch (style) {
    // ── Boots ─────────────────────────────────────────────────
    case 'uggBoot': case 'longBoot': case 'ankleBoots':
      return svg(
        // tall shaft
        r(8,2,14,14,c.base)+r(8,2,14,2,c.light)+r(8,14,14,2,c.deep)+
        r(8,2,2,14,c.shadow)+r(20,2,2,14,c.shadow)+
        // foot
        r(4,14,22,8,c.base)+r(4,14,22,2,c.light)+
        r(4,20,22,3,c.deep)+
        r(4,14,2,8,c.shadow)
      );
    // ── Rain boots ─────────────────────────────────────────────
    case 'rainBoot': case 'rainbowBoot':
      return svg(
        r(8,3,14,12,c.base)+r(8,3,14,2,c.light)+r(8,13,14,2,c.deep)+
        r(8,3,2,12,c.shadow)+r(20,3,2,12,c.shadow)+
        r(4,13,22,9,c.base)+r(4,13,22,2,c.light)+r(4,20,22,3,c.deep)+
        r(4,13,2,9,c.shadow)+
        // polka dots
        circle(11,7,2,'#ffffff',0.5)+circle(17,10,2,'#ffffff',0.5)+circle(13,12,1,'#ffffff',0.4)
      );
    // ── Mary Jane ─────────────────────────────────────────────
    case 'maryJane':
      return svg(
        r(4,13,22,8,c.base)+r(4,13,22,2,c.light)+r(4,20,22,3,c.deep)+
        r(4,13,2,8,c.shadow)+
        // T-strap
        r(13,8,6,7,c.base)+r(13,8,6,2,c.light)+
        r(8,10,16,3,c.base)+r(8,10,16,1,c.light)+r(8,12,16,1,c.deep)+
        // buckle
        circle(22,11,3,c.rim)+circle(22,11,1,'#ffffff')
      );
    // ── Heels / pump ───────────────────────────────────────────
    case 'pump': case 'dressShoe':
      return svg(
        r(6,13,20,8,c.base)+r(6,13,20,2,c.light)+
        r(6,20,20,2,c.deep)+
        r(6,13,2,8,c.shadow)+
        // heel
        r(24,18,4,8,c.deep)+r(24,18,2,8,c.shadow)+
        // toe box
        `<path d="M6,13 Q2,16 2,20 L6,20 L6,13Z" fill="${c.shadow}"/>`
      );
    // ── Sandals ────────────────────────────────────────────────
    case 'sandal': case 'flipFlop':
      return svg(
        r(4,18,24,5,c.base)+r(4,18,24,2,c.light)+r(4,22,24,2,c.deep)+
        // straps with gaps
        r(6,12,6,8,c.base)+r(8,12,2,8,c.light)+
        r(20,12,6,8,c.base)+r(22,12,2,8,c.light)+
        r(12,8,8,12,c.base)+r(14,8,4,12,c.light)+
        // toe strap
        r(4,16,24,2,c.shadow)
      );
    // ── Ballet / ballerina ─────────────────────────────────────
    case 'ballerina':
      return svg(
        r(6,15,20,8,c.base)+r(6,15,20,2,c.light)+r(6,22,20,2,c.deep)+
        r(6,15,2,8,c.shadow)+
        // ribbons
        r(10,10,4,8,c.shadow)+r(18,10,4,8,c.shadow)+
        r(8,10,8,2,c.deep)+r(16,10,8,2,c.deep)+
        // toe box (satin)
        r(4,18,6,6,c.light)+circle(5,21,3,c.light,0.3)
      );
    // ── Platform shoes ─────────────────────────────────────────
    case 'platform':
      return svg(
        r(4,10,24,10,c.base)+r(4,10,24,2,c.light)+r(4,19,24,2,c.deep)+
        r(4,10,2,10,c.shadow)+
        // thick platform sole
        r(2,19,26,8,c.shadow)+r(2,19,26,2,c.deep)+r(2,25,26,2,c.deep)
      );
    // ── Glass slipper ──────────────────────────────────────────
    case 'glassSlipper':
      return svg(
        r(6,14,20,9,c.light,0.5)+r(6,14,20,2,'#ffffff',0.3)+r(6,22,20,2,c.deep)+
        `<path d="M6,14 Q2,17 2,21 L6,21Z" fill="${c.light}" opacity="0.4"/>` +
        // sparkles
        circle(10,17,2,'#ffffff',0.7)+circle(18,16,1,'#ffffff',0.8)+circle(22,19,2,'#ffffff',0.6)+
        `<rect x="6" y="14" width="20" height="9" fill="none" stroke="${c.deep}" stroke-width="1.5" opacity="0.5"/>`
      );
    // ── High top sneaker ───────────────────────────────────────
    case 'highTop':
      return svg(
        r(8,5,14,14,c.base)+r(8,5,14,2,c.light)+r(8,17,14,2,c.deep)+
        r(8,5,2,14,c.shadow)+r(20,5,2,14,c.shadow)+
        r(2,15,24,8,c.base)+r(2,15,24,2,c.light)+r(2,22,24,3,c.deep)+
        r(2,15,2,8,c.shadow)+
        // laces
        r(10,8,2,1,c.deep)+r(14,9,2,1,c.deep)+r(10,11,2,1,c.deep)+r(14,12,2,1,c.deep)+r(10,14,2,1,c.deep)+r(14,15,2,1,c.deep)
      );
    // ── Slippers / animal slippers ─────────────────────────────
    case 'slipper': case 'slippersAnimal':
      return svg(
        r(4,17,24,8,c.base)+r(4,17,24,2,c.light)+r(4,24,24,2,c.deep)+
        r(4,17,2,8,c.shadow)+
        // plush toe area with face
        circle(8,17,7,c.base)+circle(7,15,2,'#1a1a1a')+circle(10,15,2,'#1a1a1a')+
        circle(8,18,2,'#ff9999')+
        r(6,19,1,1,'#1a1a1a')+r(9,19,1,1,'#1a1a1a')
      );
    // ── Sneakers (default) ─────────────────────────────────────
    case 'sneaker': default:
      return svg(
        r(2,11,28,8,c.shadow)+r(2,11,28,6,c.base)+r(2,11,28,2,c.light)+
        r(2,17,28,3,c.deep)+r(8,6,14,3,c.shadow)+r(9,5,12,6,c.base)+
        r(9,5,12,2,c.light)+r(4,14,4,1,c.rim,0.7)+
        r(11,9,8,1,c.deep)+r(11,11,8,1,c.deep)
      );
  }
}
